import { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './redux/store';
import { loadUserFromStorage } from './services/api/user/userApi';
import { authConfig } from './config/auth';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import paths from './routes/paths.js';
import AuthInitializer from './components/auth/AuthInitializer';
import AuthProviders from './components/auth/AuthProviders';
import Base from './components/layouts/Base';
import NotFound from './views/NotFound.jsx';
import SurveyView from './views/SurveyView';
import PupilSurveys from './views/PupilDashboard/PupilSurveys.jsx';
import LoginPage from './views/PupilDashboard/Login/LoginPage';
import EmailLinkVerify from './views/PupilDashboard/Login/EmailLinkVerify.jsx';
import QrLogin from './views/PupilDashboard/Login/QrLogin.jsx';
import MyLoginCallback from './views/PupilDashboard/Login/MyLoginCallback.jsx';
import ProtectedRoute from './routes/protectedRoutes';
import Conversations from './views/SafeSpace/Conversations.jsx'
import SelectStaff from './views/SafeSpace/SelectStaff.jsx'
import SelectZone from './views/SafeSpace/SelectZone.jsx'
import NewConversation from './views/SafeSpace/NewConversation.jsx'
import StartConversation from './views/SafeSpace/StartConversation.jsx'
import AdvConversationSuccess from './views/SafeSpace/AdvConversationSuccess.jsx'
import SimpleConversationSuccess from './views/SafeSpace/SimpleConversationSuccess.jsx'
import Questionaire from './views/CheckIn/Questionaire.jsx'
import ExternalRedirectLoginHandler from './views/PupilDashboard/Login/ExternalRedirectLoginHandler';
import EmojiLoginPage from './views/PupilDashboard/Login/EmojiLoginPage.jsx';

function App() {
  const theme = createTheme({
    typography: {
      fontFamily: ['Lexend', 'sans-serif'].join(','),
    },
    palette: {
      primary: {
        main: '#4A2A91',
        light: '#6c47c7',
        dark: '#321c63',
        contrastText: '#fff',
      },
      secondary: {
        main: '#FF7043',
        light: '#ff9166',
        dark: '#c23e16',
        contrastText: '#fff',
      },
      error: {
        main: '#F44336',
      },
      warning: {
        main: '#FFC107',
      },
      info: {
        main: '#4FC3F7',
      },
      success: {
        main: '#4CAF50',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 500,
          },
        },
      },
    },
  });

  const skipStorage = authConfig.skipRedirectStorage;
  
  useEffect(() => {
    if (!skipStorage.includes(window.location.pathname)) {
      const url = window.location.pathname + window.location.search;
      localStorage.setItem('RedirectUrl', url);
    }

    loadUserFromStorage(store);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Provider store={store}>
        <AuthProviders>
          <Router>
            <AuthInitializer />
            <Routes>
              <Route element={<Base />}>
                <Route path="/" element={<LoginPage />} />
                <Route path={paths.pupilDashLogin} element={<LoginPage />} />
                <Route path={paths.emailLogin} element={<LoginPage />} />
                <Route path={paths.emailVerify} element={<EmailLinkVerify />} />
                <Route path={paths.googleLogin} element={<LoginPage />} />
                <Route path={paths.microsoftLogin} element={<ExternalRedirectLoginHandler />} />
                <Route path={paths.myLoginCallback} element={<MyLoginCallback />} />
                <Route path={paths.qrCodeLogin} element={<QrLogin />} />
                <Route path={paths.externalLoginUrl} element={<ExternalRedirectLoginHandler />} />
                <Route path={paths.emojiLogin} element={<EmojiLoginPage />} />

                <Route element={<ProtectedRoute />}>
                  <Route path={paths.pupilSurveys} element={<PupilSurveys />} />
                  <Route path={paths.surveyView} element={<SurveyView />} />
                  <Route path={paths.conversations} element={<Conversations />} />
                  <Route path={paths.selectStaff} element={<SelectStaff />} />
                  <Route path={paths.selectZone} element={<SelectZone />} />
                  <Route path={paths.newConversation} element={<NewConversation />} />
                  <Route path={paths.startConversation} element={<StartConversation />} />
                  <Route path={paths.advConversationSuccess} element={<AdvConversationSuccess />} />   
                  <Route path={paths.simpleConversationSuccess} element={<SimpleConversationSuccess />} />                
                  <Route path={paths.checkinQuestionaire} element={<Questionaire />} />
                </Route>
                
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </Router>
        </AuthProviders>
      </Provider>
    </ThemeProvider>
  );
}

export default App;
